import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Quick Health Check - Improved Flow
 * Section 1: What are you noticing (multi-select)
 * Section 2: Basic health checks (sleep, water, food, stress, energy)
 */

const QuickHealthCheck = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Section 1: Symptoms (multi-select)
  const [symptoms, setSymptoms] = useState([]);
  
  // Section 2: Basic checks
  const [sleep, setSleep] = useState(null);
  const [water, setWater] = useState(null);
  const [food, setFood] = useState(null);
  const [stress, setStress] = useState(null);
  const [energy, setEnergy] = useState(null);
  
  // Environmental Context
  const [environment, setEnvironment] = useState(null);
  const [environmentAffected, setEnvironmentAffected] = useState(null);
  const [exposures, setExposures] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const toggleSymptom = (symptom) => {
    setSymptoms(prev => 
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const toggleExposure = (exposure) => {
    setExposures(prev =>
      prev.includes(exposure)
        ? prev.filter(e => e !== exposure)
        : [...prev, exposure]
    );
  };

  const handleSubmit = async () => {
    // Validate at least one section filled
    if (symptoms.length === 0 && !sleep && !water && !food && !stress && !energy) {
      toast.error(t('quickCheck.validation.pickOne'));
      return;
    }

    setSubmitting(true);

    // Determine overall feeling from symptoms
    let feeling = 'okay';
    if (symptoms.includes('okay')) {
      feeling = 'good';
    } else if (symptoms.includes('tired')) {
      feeling = 'tired';
    } else if (symptoms.includes('stressed')) {
      feeling = 'stressed';
    } else if (symptoms.includes('headache')) {
      feeling = 'headache';
    }

    const checkInData = {
      date: new Date().toISOString().split('T')[0],
      feeling: feeling,
      habits: {
        sleep: sleep === 'yes',
        water: water === 'yes',
        food: food === 'yes',
        movement: energy === 'high' || energy === 'okay'
      },
      additional_symptom: symptoms.join(', '),
      stress_level: stress,
      energy_level: energy,
      symptoms: symptoms,
      environment: {
        description: environment,
        affected: environmentAffected === 'yes',
        exposures: exposures
      },
      timestamp: new Date().toISOString()
    };

    try {
      // Save to legacy daily-check-in endpoint
      await axios.post(`${API}/daily-check-in`, checkInData, {
        withCredentials: true
      });

      // Also save to new Daily Health Loop system
      const habitData = {
        water: water === 'yes',
        movement: energy === 'high' || energy === 'okay',
        stress: stress === 'low' || stress === 'moderate',
        mood: symptoms.includes('okay') || (!symptoms.includes('tired') && !symptoms.includes('stressed'))
      };

      try {
        await axios.post(`${API}/daily-habits`, 
          { habits: habitData },
          { withCredentials: true }
        );
      } catch (habitError) {
        // Continue if this fails
        if (process.env.NODE_ENV === 'development') {
          console.error('Error saving to habit system:', habitError);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting check-in:', error);
      }
    }

    // Show success animation
    setShowSuccess(true);
    toast.success(t('quickCheck.submit.savedToast'));
    setSubmitting(false);

    // Navigate to Health Insight screen
    setTimeout(() => {
      navigate('/health-insight', { 
        state: { checkInData } 
      });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Success Animation */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 text-center animate-bounce-in">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-900">{t('quickCheck.submit.analyzing')}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('quickCheck.header.title')}
          </h1>
          <p className="text-gray-600">{t('quickCheck.header.subtitle')}</p>
        </div>

        {/* Section 1: What are you noticing */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">{t('quickCheck.section1.title')}</CardTitle>
            <p className="text-sm text-gray-600">(Select all that apply)</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <button
              onClick={() => toggleSymptom('tired')}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                symptoms.includes('tired')
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {symptoms.includes('tired') ? (
                  <CheckCircle2 className="h-5 w-5 text-purple-600" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium">😴 Feeling tired</span>
              </div>
            </button>

            <button
              onClick={() => toggleSymptom('headache')}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                symptoms.includes('headache')
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {symptoms.includes('headache') ? (
                  <CheckCircle2 className="h-5 w-5 text-purple-600" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium">🤕 Headache</span>
              </div>
            </button>

            <button
              onClick={() => toggleSymptom('stressed')}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                symptoms.includes('stressed')
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {symptoms.includes('stressed') ? (
                  <CheckCircle2 className="h-5 w-5 text-purple-600" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium">😰 Stressed or overwhelmed</span>
              </div>
            </button>

            <button
              onClick={() => toggleSymptom('body_aches')}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                symptoms.includes('body_aches')
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {symptoms.includes('body_aches') ? (
                  <CheckCircle2 className="h-5 w-5 text-purple-600" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium">💪 Body aches or pain</span>
              </div>
            </button>

            <button
              onClick={() => toggleSymptom('okay')}
              className={`w-full p-4 rounded-lg border-2 transition-all ${
                symptoms.includes('okay')
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {symptoms.includes('okay') ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium">😊 I feel okay today</span>
              </div>
            </button>
          </CardContent>
        </Card>

        {/* Section 1.5: Environmental Context (NEW) */}
        <Card className="border-2 border-teal-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">{t('quickCheck.environment.title')}</CardTitle>
            <p className="text-sm text-gray-600">Help us understand your surroundings</p>
          </CardHeader>
          <CardContent className="space-y-5">
            
            {/* Question 1: Environment Description */}
            <div>
              <p className="font-medium text-gray-900 mb-3">How would you describe your environment today?</p>
              <div className="space-y-2">
                <button
                  onClick={() => setEnvironment('comfortable')}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    environment === 'comfortable'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">😊 Comfortable</span>
                </button>
                
                <button
                  onClick={() => setEnvironment('slightly_uncomfortable')}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    environment === 'slightly_uncomfortable'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">😐 Slightly uncomfortable</span>
                </button>
                
                <button
                  onClick={() => setEnvironment('challenging')}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    environment === 'challenging'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">😓 Challenging</span>
                </button>
              </div>
            </div>

            {/* Question 2: Did surroundings affect you */}
            <div>
              <p className="font-medium text-gray-900 mb-3">Did your surroundings affect how you feel today?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setEnvironmentAffected('yes')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    environmentAffected === 'yes'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">Yes</span>
                </button>
                
                <button
                  onClick={() => setEnvironmentAffected('no')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    environmentAffected === 'no'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">No</span>
                </button>
              </div>
            </div>

            {/* Question 3: Exposures (multi-select) */}
            <div>
              <p className="font-medium text-gray-900 mb-3">Were you exposed to any of the following?</p>
              <p className="text-sm text-gray-600 mb-3">(Select all that apply)</p>
              <div className="space-y-2">
                <button
                  onClick={() => toggleExposure('heat')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('heat')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('heat') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>🌡️ Heat</span>
                  </div>
                </button>

                <button
                  onClick={() => toggleExposure('cold')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('cold')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('cold') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>❄️ Cold</span>
                  </div>
                </button>

                <button
                  onClick={() => toggleExposure('noise')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('noise')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('noise') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>🔊 Noise</span>
                  </div>
                </button>

                <button
                  onClick={() => toggleExposure('dust_smoke')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('dust_smoke')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('dust_smoke') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>💨 Dust or smoke</span>
                  </div>
                </button>

                <button
                  onClick={() => toggleExposure('crowded')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('crowded')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('crowded') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>👥 Crowded space</span>
                  </div>
                </button>

                <button
                  onClick={() => toggleExposure('none')}
                  className={`w-full p-3 rounded-lg border-2 transition-all ${
                    exposures.includes('none')
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {exposures.includes('none') ? (
                      <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span>✅ None</span>
                  </div>
                </button>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Section 2: Let's check your basics */}
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Let's check your basics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sleep */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Did you sleep well last night?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSleep('yes')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    sleep === 'yes'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Yes</span>
                </button>
                <button
                  onClick={() => setSleep('no')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    sleep === 'no'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Not really</span>
                </button>
              </div>
            </div>

            {/* Water */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Have you had enough water today?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setWater('yes')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    water === 'yes'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Yes</span>
                </button>
                <button
                  onClick={() => setWater('no')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    water === 'no'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Not yet</span>
                </button>
              </div>
            </div>

            {/* Food */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Have you eaten properly today?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setFood('yes')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    food === 'yes'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Yes</span>
                </button>
                <button
                  onClick={() => setFood('no')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    food === 'no'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Not yet</span>
                </button>
              </div>
            </div>

            {/* Stress */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">How is your stress level today?</p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setStress('low')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    stress === 'low'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">Low</span>
                </button>
                <button
                  onClick={() => setStress('moderate')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    stress === 'moderate'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-200 hover:border-yellow-300'
                  }`}
                >
                  <span className="font-medium">Moderate</span>
                </button>
                <button
                  onClick={() => setStress('high')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    stress === 'high'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300'
                  }`}
                >
                  <span className="font-medium">High</span>
                </button>
              </div>
            </div>

            {/* Energy */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">How is your energy today?</p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setEnergy('low')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    energy === 'low'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Low</span>
                </button>
                <button
                  onClick={() => setEnergy('okay')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    energy === 'okay'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium">Okay</span>
                </button>
                <button
                  onClick={() => setEnergy('high')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    energy === 'high'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">High</span>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {t('quickCheck.submit.analyzing')}
            </>
          ) : (
            t('quickCheck.submit.cta')
          )}
        </Button>

        {/* Skip Option */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {t('onboarding.skipForNow', 'Skip for now')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickHealthCheck;
